import { directIfcExports, indirectIfcExports } from '../constants/ifcFilter'
import type {
  IfcElementTypeAssignment,
  IfcModelMetadata,
  IfcStoreyAssignment,
} from '../types/ifcMetadata'

export function getGuidToAssignedElementType(
  metadata: IfcModelMetadata | null,
): Record<string, string> {
  const out: Record<string, string> = {}

  for (const group of metadata?.elementTypeAssignments ?? []) {
    for (const assignment of group) {
      out[assignment.elementId] = assignment.elementType
    }
  }

  return out
}

export function upsertElementTypeAssignment(
  assignments: IfcModelMetadata['elementTypeAssignments'],
  nextAssignment: IfcElementTypeAssignment,
): IfcElementTypeAssignment[][] {
  return [
    ...(assignments ?? [])
      .map(group => group.filter(assignment => assignment.elementId !== nextAssignment.elementId))
      .filter(group => group.length > 0),
    [nextAssignment],
  ]
}

export function upsertStoreyAssignment(
  assignments: IfcModelMetadata['storeyAssignments'],
  nextAssignment: IfcStoreyAssignment,
): IfcStoreyAssignment[][] {
  return [
    ...(assignments ?? [])
      .map(group => group.filter(assignment => assignment.elementId !== nextAssignment.elementId))
      .filter(group => group.length > 0),
    [nextAssignment],
  ]
}

/**
 * Map each element guid to its MGroup (element type) from IFC metadata.
 */
export function getGuidToMGroup(metadata: IfcModelMetadata | null): Record<string, string> {
  const out: Record<string, string> = {}
  for (const [guid, entry] of Object.entries(metadata?.property_data?.data ?? {})) {
    const mgroup = entry?.PSET_data?.MGroup
    if (typeof mgroup === 'string') out[guid] = mgroup
  }
  Object.assign(out, getGuidToAssignedElementType(metadata))
  return out
}

/**
 * Unique MGroup types that are in directIfcExports (imported IFC types).
 */
export function getImportedTypes(metadata: IfcModelMetadata | null): string[] {
  const set = new Set<string>()
  for (const mgroup of Object.values(getGuidToMGroup(metadata))) {
    if ((directIfcExports as readonly string[]).includes(mgroup)) {
      set.add(mgroup)
    }
  }
  return Array.from(set).sort()
}

/**
 * Unique MGroup types that are neither direct nor indirect exports (not imported / not relevant).
 */
export function getNotImportedTypes(metadata: IfcModelMetadata | null): string[] {
  const set = new Set<string>()
  for (const mgroup of Object.values(getGuidToMGroup(metadata))) {
    const direct = (directIfcExports as readonly string[]).includes(mgroup)
    const indirect = (indirectIfcExports as readonly string[]).includes(mgroup)
    if (!direct && !indirect) {
      set.add(mgroup)
    }
  }
  return Array.from(set).sort()
}

/**
 * Build guid -> hidden from visibility-by-type. A guid is hidden if its MGroup is not visible.
 */
export function getGuidToHiddenFromTypes(
  guidToMGroup: Record<string, string>,
  visibilityByType: Record<string, boolean>,
): Record<string, boolean> {
  const out: Record<string, boolean> = {}
  for (const [guid, mgroup] of Object.entries(guidToMGroup)) {
    if (visibilityByType[mgroup] === false) out[guid] = true
  }
  return out
}

/**
 * Build guid -> hidden from explicitly filtered IFC element groups.
 */
export function getGuidToHiddenFromFilteredElements(
  filteredElements: IfcModelMetadata['filteredElements'],
): Record<string, boolean> {
  const out: Record<string, boolean> = {}

  for (const group of filteredElements ?? []) {
    for (const guid of group) {
      out[guid] = true
    }
  }

  return out
}

/**
 * Map each storey-assigned IFC element guid to its storey.
 */
export function getGuidToStorey(
  storeyAssignments: IfcStoreyAssignment[][] | undefined,
): Record<string, string> {
  const out: Record<string, string> = {}
  for (const group of storeyAssignments ?? []) {
    for (const { elementId, storey } of group) {
      out[elementId] = storey
    }
  }
  return out
}

/**
 * Build guid -> hidden from per-storey type visibility: an element is hidden when its type is
 * toggled off on its assigned storey. Elements without a storey assignment are unaffected.
 */
export function getGuidToHiddenFromStoreyTypes(
  guidToMGroup: Record<string, string>,
  guidToStorey: Record<string, string>,
  visibilityByStoreyAndType: Record<string, Record<string, boolean>>,
): Record<string, boolean> {
  const out: Record<string, boolean> = {}
  for (const [guid, mgroup] of Object.entries(guidToMGroup)) {
    const storey = guidToStorey[guid]
    if (storey && visibilityByStoreyAndType[storey]?.[mgroup] === false) out[guid] = true
  }
  return out
}
